import React, { useEffect, useMemo, useState } from "react";
import { watchJobs, jobDisplayNumber } from "../../services/firestore";
import { combineJobs } from "../../services/intake";

// "Same run?" — Ferrari jobs that share a date, so the owner can combine the
// ones that were one run into a single job (one job per order otherwise).
// Nothing is combined without a click; combining cancels the other jobs with
// a pointer (never deletes). Design: docs/ferrari-intake-design.md.

const DISMISS_KEY = "veloceflow.sameRun.dismissed";
const LOOKBACK_DAYS = 60;

const cell = { padding: "6px 10px", fontSize: 12, borderBottom: "1px solid #f0f0f0" };

function readDismissed() {
  try { return new Set(JSON.parse(localStorage.getItem(DISMISS_KEY) || "[]")); } catch { return new Set(); }
}
function writeDismissed(set) {
  try { localStorage.setItem(DISMISS_KEY, JSON.stringify([...set])); } catch { /* storage unavailable */ }
}

const fmtDate = (ymd) => {
  const [y, m, d] = String(ymd).split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
};
// "Oct 6, 7, 19, 21, 27" — the month only where it changes.
function dayList(dates) {
  let lastMonth = null;
  return dates.map((ymd) => {
    const [y, m, d] = String(ymd).split("-").map(Number);
    const mon = new Date(y, m - 1, d).toLocaleDateString(undefined, { month: "short" });
    const out = mon === lastMonth ? String(d) : `${mon} ${d}`;
    lastMonth = mon;
    return out;
  }).join(", ");
}
const localStops = (j) => (j.stops || []).filter((s) => s.type !== "airport");

function Group({ date, jobs, onDismiss, onOpenJob }) {
  const [picked, setPicked] = useState(() => new Set(jobs.map((j) => j.id)));
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const chosen = jobs.filter((j) => picked.has(j.id));
  const types = new Set(chosen.map((j) => j.jobType));
  const mixed = types.size > 1;

  // On a mixed day the owner can also combine just one direction
  // (e.g. two outbound pickups were one run, the inbound was separate).
  const byType = (t) => chosen.filter((j) => j.jobType === t);
  const inbound = byType("inbound");
  const outbound = byType("outbound");

  async function run(asRoundtrip, list = chosen) {
    setBusy(true);
    setMsg("");
    try {
      // Job numbers are internal for Ferrari, so the first job is kept.
      await combineJobs(list.map((j) => j.id), asRoundtrip);
      setMsg("Combined into one job");
      // Combining one direction leaves the rest of the day to work on.
      if (list !== chosen) setBusy(false);
    } catch (e) {
      setMsg(e?.message || String(e));
      setBusy(false);
    }
  }

  const toggle = (id) => setPicked((p) => {
    const n = new Set(p);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });

  return (
    <div style={{ borderBottom: "1px solid #e8e8e8", padding: "6px 0" }}>
      <div style={{ ...cell, display: "flex", gap: 10, alignItems: "center", borderBottom: "none", fontWeight: 700 }}>
        {fmtDate(date)} · {jobs.length} Ferrari jobs
        <span style={{ marginLeft: "auto", display: "flex", gap: 6, alignItems: "center", fontWeight: 400 }}>
          {msg && <span style={{ fontSize: 11, color: "#555" }}>{msg}</span>}
          {!mixed && (
            <button className="btn btn-sm btn-blue" disabled={busy || chosen.length < 2} onClick={() => run(false)}>
              Combine {chosen.length} into one job
            </button>
          )}
          {mixed && outbound.length > 1 && (
            <button className="btn btn-sm btn-blue" disabled={busy} onClick={() => run(false, outbound)}>
              Combine {outbound.length} outbound only
            </button>
          )}
          {mixed && inbound.length > 1 && (
            <button className="btn btn-sm btn-blue" disabled={busy} onClick={() => run(false, inbound)}>
              Combine {inbound.length} inbound only
            </button>
          )}
          {mixed && (
            <button className="btn btn-sm btn-blue" disabled={busy || chosen.length < 2} onClick={() => run(true)}>
              Combine all as round trip
            </button>
          )}
          <button className="btn btn-sm" disabled={busy} onClick={onDismiss}>Not one run</button>
        </span>
      </div>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <tbody>
          {jobs.map((j) => (
            <tr key={j.id} style={{ opacity: picked.has(j.id) ? 1 : 0.45 }}>
              <td style={{ ...cell, width: 24 }}>
                <input type="checkbox" checked={picked.has(j.id)} disabled={busy} onChange={() => toggle(j.id)} />
              </td>
              <td style={{ ...cell, fontWeight: 600 }}>
                <a href="#job" onClick={(e) => { e.preventDefault(); onOpenJob(j.id); }}>{jobDisplayNumber(j)}</a>
              </td>
              <td style={cell}>{j.jobType}</td>
              <td style={cell}>{j.status}</td>
              {/* The address is what tells you whether two orders were one run. */}
              <td style={cell}>
                {localStops(j).length === 0 ? "—" : localStops(j).map((s) => (
                  <div key={s.id || s.locationName} style={{ marginBottom: 2 }}>
                    {s.locationName || "—"}
                    {s.address && <div style={{ fontSize: 11, color: "#6b7684" }}>{s.address}</div>}
                  </div>
                ))}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function SameRunPanel({ companyId, onOpenJob }) {
  const [jobs, setJobs] = useState([]);
  const [dismissed, setDismissed] = useState(readDismissed);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!companyId) return undefined;
    return watchJobs(companyId, null, setJobs);
  }, [companyId]);

  const groups = useMemo(() => {
    const cutoff = new Date(Date.now() - LOOKBACK_DAYS * 86400000).toISOString().slice(0, 10);
    const byDate = {};
    for (const j of jobs) {
      if (!/ferrari/i.test(j.brokerName || "")) continue;
      if (j.status === "cancelled" || j.combinedInto) continue;
      if (j.invoiceStatus && j.invoiceStatus !== "unpaid") continue;
      if (!j.scheduledDate || j.scheduledDate < cutoff) continue;
      (byDate[j.scheduledDate] = byDate[j.scheduledDate] || []).push(j);
    }
    return Object.entries(byDate)
      .filter(([, list]) => list.length > 1)
      .map(([date, list]) => {
        const sorted = list.slice().sort((a, b) => (a.jobType === b.jobType ? jobDisplayNumber(a).localeCompare(jobDisplayNumber(b)) : a.jobType === "inbound" ? -1 : 1));
        return { date, jobs: sorted, key: `${date}|${sorted.map((j) => j.id).sort().join(",")}` };
      })
      .filter((g) => !dismissed.has(g.key))
      .sort((a, b) => a.date.localeCompare(b.date));
  }, [jobs, dismissed]);

  function dismiss(key) {
    const next = new Set(dismissed);
    next.add(key);
    writeDismissed(next);
    setDismissed(next);
  }

  if (!groups.length) return null;
  // Folded to one row until opened, so it doesn't push the page's own
  // work below the fold. The row lists the days waiting.
  const header = (
    <button
      onClick={() => setOpen((v) => !v)}
      style={{
        width: "100%", background: "#fff", border: "1px solid #e3e6ea", borderRadius: open ? "10px 10px 0 0" : 10,
        padding: "11px 14px", marginBottom: open ? 0 : 12, display: "flex", justifyContent: "space-between",
        alignItems: "center", gap: 10, fontSize: 13.5, cursor: "pointer", textAlign: "left", fontFamily: "inherit",
      }}
    >
      <span>
        <b>Same run?</b> · {groups.length} day{groups.length !== 1 ? "s" : ""} to decide
        <span style={{ color: "#6b7684" }}> ({dayList(groups.map((g) => g.date))})</span>
      </span>
      <span style={{ color: "#185FA5", fontWeight: 800, whiteSpace: "nowrap" }}>{open ? "Close" : "Open ›"}</span>
    </button>
  );
  if (!open) return header;
  return (
    <div style={{ marginBottom: 12 }}>
      {header}
    <div className="panel" style={{ borderTopLeftRadius: 0, borderTopRightRadius: 0, borderTop: "none" }}>
      <div style={{ padding: "8px 14px", fontSize: 12, color: "#6b7684", borderBottom: "1px solid #f0f0f0" }}>
        Ferrari jobs on the same date. Tick the ones that were one run and combine them; untick any that were separate.
      </div>
      {groups.map((g) => (
        <Group key={g.key} date={g.date} jobs={g.jobs} onDismiss={() => dismiss(g.key)} onOpenJob={onOpenJob} />
      ))}
    </div>
    </div>
  );
}
